import { useMemo } from 'react';
import type { HostAnamnesis } from '@meddihub/mzcr-ui';
import apiClient from '../../../Services/ApiClient';
import { isApiSuccess } from '../../../Utils/http.util';
import { useAppContext } from '../../../Services/AppContext';
import {
	useGetUserOriginalIdQuery,
	useLazyMedicalHistoryTypeQuery,
} from '../../../graphql/graphql';
import useLoadMedicalHistoryDocument from '../../Patient/Hooks/useLoadMedicalHistoryDocument';
import {
	MEDICAL_HISTORY_TYPES_TAKE,
	buildMedicalHistoryItems,
	createMedicalHistoryDocument,
	toMedicalHistoryChild,
} from '../../Patient/Services/medicalHistory.service';

/**
 * The patient's anamnesis (Léčba → Anamnéza) for the MZČR shared health record comparison
 * (design S5). Same storage as `usePatientMedicalHistory` (shared in `medicalHistory.service`):
 * one document with a text per section, every change is saved as a new version
 * (`/document/{id}/copy`).
 *
 * Call it only once the MZČR service is shown - it loads the patient right away.
 */
export default function useMzcrAnamnesis(
	patientId: string,
	onOpen?: () => void,
): HostAnamnesis | undefined {
	const { workplaceId, userData } = useAppContext();
	const user = useGetUserOriginalIdQuery({ id: patientId });
	const originalId = user.data?.user?.originalId ?? undefined;
	const [getMedicalHistoryType] = useLazyMedicalHistoryTypeQuery();
	const loadMedicalHistoryDocument = useLoadMedicalHistoryDocument();

	return useMemo(() => {
		if (!originalId) {
			return undefined;
		}

		const loadAnamnesis = async () => {
			const [types, document] = await Promise.all([
				getMedicalHistoryType({
					where: { workplaceId, active: true },
					pagination: { take: MEDICAL_HISTORY_TYPES_TAKE },
				}),
				loadMedicalHistoryDocument(originalId),
			]);
			return {
				documentId: document?.id,
				items: buildMedicalHistoryItems(
					types.data?.medicalHistoryType?.data ?? [],
					document?.items,
				),
			};
		};

		return {
			load: async () =>
				(await loadAnamnesis()).items.map(item => ({
					key: item.key,
					title: item.title,
					text: item.value,
					order: item.order,
				})),
			append: async additions => {
				const current = await loadAnamnesis();
				const documentId =
					current.documentId ??
					(await createMedicalHistoryDocument({
						workplaceId,
						patientOriginalId: originalId,
						createdById: userData.id,
					}));
				if (!documentId) {
					throw new Error('Anamnesis document could not be created');
				}
				// The whole anamnesis goes into the new version - the untouched sections unchanged
				const children = current.items.map(item => {
					const lines = additions
						.filter(addition => addition.sectionKey === item.key)
						.map(addition => addition.text);
					const text = lines.length
						? [item.value.trim(), ...lines].filter(Boolean).join('\n')
						: item.value;
					return toMedicalHistoryChild(item, text);
				});
				const response = await apiClient.post(`/document/${documentId}/copy`, {
					children,
				});
				if (!isApiSuccess(response)) {
					throw new Error('Anamnesis could not be saved');
				}
			},
			open: onOpen,
		};
	}, [
		originalId,
		workplaceId,
		userData.id,
		onOpen,
		loadMedicalHistoryDocument,
	]);
}
